/**
 * Enhanced Search Analytics
 * Track search performance and usage patterns for Phase 2
 */

interface SearchMetrics {
  query: string;
  timestamp: number;
  tab: 'all' | 'movies' | 'watchlists' | 'users' | 'people';
  mode: 'basic' | 'enhanced' | 'auto-enhanced';
  resultCount: number;
  loadTimeMs: number;
  wasBackgroundEnhanced: boolean;
  trigger?: 'user-toggle' | 'auto-detect' | 'zero-results' | 'background';
}

interface AggregatedMetrics {
  totalSearches: number;
  basicSearches: number;
  enhancedSearches: number;
  autoEnhancedSearches: number;
  backgroundEnhancedSearches: number;
  averageLoadTimeBasic: number;
  averageLoadTimeEnhanced: number;
  enhancedUsageRate: number; // %
  backgroundEnhancementSuccessRate: number; // %
  zeroResultRate: number; // %
}

class EnhancedSearchAnalytics {
  private metrics: SearchMetrics[] = [];
  private readonly MAX_METRICS = 1000; // Keep last 1000 searches

  /**
   * Track a search event
   */
  trackSearch(params: {
    query: string;
    tab: SearchMetrics['tab'];
    mode: SearchMetrics['mode'];
    resultCount: number;
    loadTimeMs: number;
    wasBackgroundEnhanced?: boolean;
    trigger?: SearchMetrics['trigger'];
  }): void {
    const metric: SearchMetrics = {
      query: params.query,
      timestamp: Date.now(),
      tab: params.tab,
      mode: params.mode,
      resultCount: params.resultCount,
      loadTimeMs: params.loadTimeMs,
      wasBackgroundEnhanced: params.wasBackgroundEnhanced || false,
      trigger: params.trigger,
    };

    this.metrics.push(metric);

    // Keep only recent metrics
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics = this.metrics.slice(-this.MAX_METRICS);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Search Analytics:', metric);
    }
  }

  /**
   * Get aggregated metrics
   */
  getAggregatedMetrics(timeRangeMs?: number): AggregatedMetrics {
    const now = Date.now();
    const relevantMetrics = timeRangeMs
      ? this.metrics.filter(m => now - m.timestamp <= timeRangeMs)
      : this.metrics;

    if (relevantMetrics.length === 0) {
      return {
        totalSearches: 0,
        basicSearches: 0,
        enhancedSearches: 0,
        autoEnhancedSearches: 0,
        backgroundEnhancedSearches: 0,
        averageLoadTimeBasic: 0,
        averageLoadTimeEnhanced: 0,
        enhancedUsageRate: 0,
        backgroundEnhancementSuccessRate: 0,
        zeroResultRate: 0,
      };
    }

    const totalSearches = relevantMetrics.length;
    const basicSearches = relevantMetrics.filter(m => m.mode === 'basic').length;
    const enhancedSearches = relevantMetrics.filter(m => m.mode === 'enhanced').length;
    const autoEnhancedSearches = relevantMetrics.filter(m => m.mode === 'auto-enhanced').length;
    const backgroundEnhancedSearches = relevantMetrics.filter(m => m.wasBackgroundEnhanced).length;

    const basicLoadTimes = relevantMetrics
      .filter(m => m.mode === 'basic')
      .map(m => m.loadTimeMs);

    const enhancedLoadTimes = relevantMetrics
      .filter(m => m.mode === 'enhanced' || m.mode === 'auto-enhanced')
      .map(m => m.loadTimeMs);

    const averageLoadTimeBasic = basicLoadTimes.length > 0
      ? basicLoadTimes.reduce((a, b) => a + b, 0) / basicLoadTimes.length
      : 0;

    const averageLoadTimeEnhanced = enhancedLoadTimes.length > 0
      ? enhancedLoadTimes.reduce((a, b) => a + b, 0) / enhancedLoadTimes.length
      : 0;

    const enhancedUsageRate = totalSearches > 0
      ? ((enhancedSearches + autoEnhancedSearches) / totalSearches) * 100
      : 0;

    const backgroundEnhancementSuccessRate = backgroundEnhancedSearches > 0
      ? (backgroundEnhancedSearches / totalSearches) * 100
      : 0;

    const zeroResultCount = relevantMetrics.filter(m => m.resultCount === 0).length;
    const zeroResultRate = totalSearches > 0
      ? (zeroResultCount / totalSearches) * 100
      : 0;

    return {
      totalSearches,
      basicSearches,
      enhancedSearches,
      autoEnhancedSearches,
      backgroundEnhancedSearches,
      averageLoadTimeBasic: Math.round(averageLoadTimeBasic),
      averageLoadTimeEnhanced: Math.round(averageLoadTimeEnhanced),
      enhancedUsageRate: Math.round(enhancedUsageRate * 10) / 10,
      backgroundEnhancementSuccessRate: Math.round(backgroundEnhancementSuccessRate * 10) / 10,
      zeroResultRate: Math.round(zeroResultRate * 10) / 10,
    };
  }

  /**
   * Get metrics summary for console logging
   */
  logSummary(): void {
    const last24Hours = 24 * 60 * 60 * 1000;
    const metrics = this.getAggregatedMetrics(last24Hours);

    console.group('📊 Search Analytics Summary (Last 24 Hours)');
    console.log('Total Searches:', metrics.totalSearches);
    console.log('Basic Searches:', `${metrics.basicSearches} (${100 - metrics.enhancedUsageRate}%)`);
    console.log('Enhanced Searches:', `${metrics.enhancedSearches + metrics.autoEnhancedSearches} (${metrics.enhancedUsageRate}%)`);
    console.log('  - Manual:', metrics.enhancedSearches);
    console.log('  - Auto-detected:', metrics.autoEnhancedSearches);
    console.log('Background Enhanced:', `${metrics.backgroundEnhancedSearches} (${metrics.backgroundEnhancementSuccessRate}%)`);
    console.log('Average Load Time:');
    console.log('  - Basic:', `${metrics.averageLoadTimeBasic}ms`);
    console.log('  - Enhanced:', `${metrics.averageLoadTimeEnhanced}ms`);
    console.log('Zero Result Rate:', `${metrics.zeroResultRate}%`);
    console.groupEnd();
  }

  /**
   * Export metrics for external analytics (e.g., send to backend)
   */
  exportMetrics(timeRangeMs?: number): SearchMetrics[] {
    const now = Date.now();
    return timeRangeMs
      ? this.metrics.filter(m => now - m.timestamp <= timeRangeMs)
      : [...this.metrics];
  }

  /**
   * Clear all metrics (for testing)
   */
  clearMetrics(): void {
    this.metrics = [];
  }
}

// Export singleton instance
export const enhancedSearchAnalytics = new EnhancedSearchAnalytics();

// Export class for testing
export { EnhancedSearchAnalytics };
